import { Alert, Button, Card, Col, Descriptions, Row, Space, Statistic, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Dashboard = {
  scenario_counts: Record<string, number>;
  run_counts: Record<string, number>;
  last_run?: {
    id: string;
    status: string;
    solver_status?: string | null;
    completed_at?: string | null;
    error_message?: string | null;
  } | null;
  solver_availability: { engine: string; status: string; notes?: string[] };
  permissions: { view: boolean; manage: boolean; publish: boolean };
};

export default function SmartTimetableStudioPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['education-smart-tt-dashboard'],
    queryFn: () => api.get<Dashboard>('/education/timetable/smart/dashboard'),
  });
  const data = query.data?.data;
  const solverStatus = data?.solver_availability?.status || 'UNKNOWN';

  return (
    <EducationPageChrome
      title={t('smartTimetableStudio', { defaultValue: 'Smart Timetable Studio' })}
      description={t('smartTimetableStudioIntro', {
        defaultValue:
          'Generate, compare, adjust and publish timetable scenarios. Candidates never mutate published grids until an authorized publish.',
      })}
      extra={
        <Space wrap>
          <Button onClick={() => navigate('/education/timetable')}>{t('timetable')}</Button>
          <Button type="primary" onClick={() => navigate('/education/timetable/smart/setup')}>
            {t('schedulingSetup', { defaultValue: 'Scheduling setup' })}
          </Button>
          <Button onClick={() => navigate('/education/timetable/smart/scenarios')}>
            {t('generationScenarios', { defaultValue: 'Scenarios' })}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type={
            solverStatus === 'UNAVAILABLE' || solverStatus === 'ERROR'
              ? 'warning'
              : solverStatus === 'OPTIMAL' || solverStatus === 'FEASIBLE'
                ? 'success'
                : 'info'
          }
          showIcon
          message={t('solverStatus', { defaultValue: 'Solver status' })}
          description={`${data?.solver_availability?.engine || 'node_heuristic'}: ${solverStatus}. ${(data?.solver_availability?.notes || []).join(' ')}`}
        />
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card size="small">
              <Statistic title={t('draftScenarios', { defaultValue: 'Draft scenarios' })} value={data?.scenario_counts?.draft || 0} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small">
              <Statistic
                title={t('validatedScenarios', { defaultValue: 'Validated scenarios' })}
                value={data?.scenario_counts?.validated || 0}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small">
              <Statistic
                title={t('publishedScenarios', { defaultValue: 'Published scenarios' })}
                value={data?.scenario_counts?.published || 0}
              />
            </Card>
          </Col>
        </Row>
        <Card size="small" title={t('lastGeneration', { defaultValue: 'Last generation run' })} loading={query.isLoading}>
          {data?.last_run ? (
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="ID">{data.last_run.id}</Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                <Tag>{data.last_run.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('solverStatus', { defaultValue: 'Solver' })}>
                <Tag color={data.last_run.solver_status === 'OPTIMAL' || data.last_run.solver_status === 'FEASIBLE' ? 'green' : 'default'}>
                  {data.last_run.solver_status || '—'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('completedAt', { defaultValue: 'Completed' })}>
                {data.last_run.completed_at || '—'}
              </Descriptions.Item>
              {data.last_run.error_message ? (
                <Descriptions.Item label={t('error', { defaultValue: 'Error' })}>
                  <Typography.Text type="danger">{data.last_run.error_message}</Typography.Text>
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          ) : (
            <Typography.Text type="secondary">
              {t('noGenerationYet', { defaultValue: 'No generation runs yet.' })}
            </Typography.Text>
          )}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
